import * as React from 'react';
import { Children, cloneElement, isValidElement, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslate, useGetIdentity } from 'ra-core';
import {
  Tooltip,
  IconButton,
  Menu,
  Button,
  Avatar,
  PopoverOrigin,
  Box,
  SxProps,
  useTheme,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { UserIdentity } from 'react-admin';
import { UserFields } from '../shared/constants/users';
import { getFirestore } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

const defaultIcon = <AccountCircle sx={{ width: 32, height: 32 }} />;

const AnchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'right',
};

const TransformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
};

const UserMenu = (
  props: UserMenuProps & {
    sx?: SxProps;
  }
) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const translate = useTranslate();
  const { isLoading, identity = {} as UserIdentity } = useGetIdentity();
  const theme = useTheme();

  const sx = {
    user: {},
    userButton: {
      textTransform: 'none',
      ml: 1,
    },
    avatar: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      borderRadius: '50%',
    },
  };

  const [avatar, setAvatar] = useState('');
  const { last_name = '', first_name = '', email = '' } = identity || {};
  const name = `${first_name} ${last_name}`.trim() || email;

  const { children, label = 'ra.auth.user_menu', icon = defaultIcon, logout } = props;

  useEffect(() => {
    const fetchImage = async () => {
      if (identity[UserFields.ProfilePicture]) {
        const url = await getDownloadURL(
          ref(getStorage(), identity[UserFields.ProfilePicture])
        );
        setAvatar(url);
      } else {
        setAvatar('');
      }
    };

    fetchImage();
  }, [identity[UserFields.ProfilePicture]]);

  if (!logout && !children) return null;
  const open = Boolean(anchorEl);

  const handleMenu = (event: any) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <Box sx={[sx.user, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}>
      {!isLoading && identity?.[UserFields.Username] ? (
        <IconButton
          sx={sx.userButton}
          onClick={handleMenu}
          aria-label={label && translate(label, { _: label })}
          color="inherit"
        >
          {avatar ? (
            <Avatar
              sx={[sx.avatar, { border: '1px solid', borderColor: 'info.600' }]}
              src={avatar}
              alt={identity.fullName}
            />
          ) : (
            icon
          )}
        </IconButton>
      ) : (
        // <Button
        //   aria-label={label && translate(label, { _: label })}
        //   sx={sx.userButton}
        //   color="inherit"
        //   startIcon={
        //     avatar ? (
        //       <Avatar
        //         sx={[sx.avatar, { border: '1px solid', borderColor: 'info.600' }]}
        //         src={avatar}
        //         alt={identity.fullName}
        //       />
        //     ) : (
        //       icon
        //     )
        //   }
        //   onClick={handleMenu}
        // >
        //   {name}
        // </Button>
        <Tooltip title={label && translate(label, { _: label })}>
          <IconButton
            aria-label={label && translate(label, { _: label })}
            aria-owns={open ? 'menu-appbar' : undefined}
            aria-haspopup={true}
            color="inherit"
            onClick={handleMenu}
            size="large"
          >
            {icon}
          </IconButton>
        </Tooltip>
      )}
      <Menu
        id="menu-appbar"
        disableScrollLock
        anchorEl={anchorEl}
        anchorOrigin={AnchorOrigin}
        transformOrigin={TransformOrigin}
        open={open}
        onClose={handleClose}
        // sx={{ width: '500px' }}
      >
        {Children.map(children, (menuItem) =>
          isValidElement(menuItem)
            ? cloneElement<any>(menuItem, {
                onClick: handleClose,
              })
            : null
        )}
        {logout}
      </Menu>
    </Box>
  );
};

UserMenu.propTypes = {
  children: PropTypes.node,
  classes: PropTypes.object,
  label: PropTypes.string,
  logout: PropTypes.element,
  icon: PropTypes.node,
};

export interface UserMenuProps {
  children?: React.ReactNode;
  classes?: {};
  label?: string;
  logout?: React.ReactNode;
  icon?: React.ReactNode;
}

export default UserMenu;
